import React, { type FC, useMemo } from "react";

import { DarkThemeSingleSelect } from "../../../../components";
import {
  getReportBuilderSelectStyles,
  REPORT_BUILDER_SELECT_COMPONENTS,
} from "../shared/rebootLiveSelectStyles";

import {
  ANALYSIS_TONE_OPTIONS,
  OUTPUT_FORMAT_OPTIONS,
  RECOMMENDATIONS_OPTIONS,
  TARGET_AUDIENCE_OPTIONS,
} from "./analysisBuilderConstants";

export interface ReportSettings {
  analysisTone: string;
  targetAudience: string;
  outputFormat: string;
  includeRecommendations: string;
  additionalContext: string;
}

interface GlobalReportSettingsProps {
  settings: ReportSettings;
  onSettingsChange: (settings: ReportSettings) => void;
  isDisabled?: boolean;
}

export const GlobalReportSettings: FC<GlobalReportSettingsProps> = ({
  settings,
  onSettingsChange,
  isDisabled = false,
}) => {
  const selectStyles = useMemo(() => getReportBuilderSelectStyles(), []);

  const handleChange = (field: keyof ReportSettings, value: string) => {
    onSettingsChange({
      ...settings,
      [field]: value,
    });
  };

  return (
    <div
      className={`bg-[#1d1d1f] border border-white/10 rounded-lg p-6 flex flex-col gap-4 ${
        isDisabled ? "opacity-50 pointer-events-none" : ""
      }`}
      data-testid="global-report-settings"
    >
      <h2
        className="text-white font-medium"
        data-testid="global-report-settings-title"
      >
        Global Report Settings
      </h2>

      <div className="grid grid-cols-2 gap-4">
        {/* Analysis Tone */}
        <div>
          <label className="text-[#b6b2b2] text-sm block mb-2">
            Analysis Tone
          </label>
          <DarkThemeSingleSelect
            value={settings.analysisTone}
            onChange={(value) => handleChange("analysisTone", value)}
            options={ANALYSIS_TONE_OPTIONS}
            placeholder="Select Tone"
            styles={selectStyles}
            className="w-full"
            classNamePrefix="report-settings-tone"
            customComponents={REPORT_BUILDER_SELECT_COMPONENTS}
            isDisabled={isDisabled}
          />
        </div>

        {/* Target Audience */}
        <div>
          <label className="text-[#b6b2b2] text-sm block mb-2">
            Target Audience
          </label>
          <DarkThemeSingleSelect
            value={settings.targetAudience}
            onChange={(value) => handleChange("targetAudience", value)}
            options={TARGET_AUDIENCE_OPTIONS}
            placeholder="Select Audience"
            styles={selectStyles}
            className="w-full"
            classNamePrefix="report-settings-audience"
            customComponents={REPORT_BUILDER_SELECT_COMPONENTS}
            isDisabled={isDisabled}
          />
        </div>

        {/* Output Format */}
        <div>
          <label className="text-[#b6b2b2] text-sm block mb-2">
            Output Format
          </label>
          <DarkThemeSingleSelect
            value={settings.outputFormat}
            onChange={(value) => handleChange("outputFormat", value)}
            options={OUTPUT_FORMAT_OPTIONS}
            placeholder="Select Format"
            styles={selectStyles}
            className="w-full"
            classNamePrefix="report-settings-format"
            customComponents={REPORT_BUILDER_SELECT_COMPONENTS}
            isDisabled={isDisabled}
          />
        </div>

        {/* Include Recommendations */}
        <div>
          <label className="text-[#b6b2b2] text-sm block mb-2">
            Include Recommendations
          </label>
          <DarkThemeSingleSelect
            value={settings.includeRecommendations}
            onChange={(value) => handleChange("includeRecommendations", value)}
            options={RECOMMENDATIONS_OPTIONS}
            placeholder="Select Recommendation"
            styles={selectStyles}
            className="w-full"
            classNamePrefix="report-settings-recommendations"
            customComponents={REPORT_BUILDER_SELECT_COMPONENTS}
            isDisabled={isDisabled}
          />
        </div>
      </div>

      {/* Additional Context */}
      <div>
        <label
          htmlFor="global-report-settings-additional-context"
          className="text-[#b6b2b2] text-sm block mb-2"
        >
          Additional Context (athlete info, injury history, goals, etc.)
        </label>
        <textarea
          id="global-report-settings-additional-context"
          value={settings.additionalContext}
          onChange={(e) => handleChange("additionalContext", e.target.value)}
          placeholder="e.g., Right-handed pitcher, college sophomore, recovering from minor shoulder fatigue, working on increasing velocity..."
          className="w-full bg-[#0b0b0b] border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-[#979797] min-h-[80px] resize-y focus:border-orange-500 focus:outline-none transition-colors text-sm"
          disabled={isDisabled}
          data-testid="global-report-settings-additional-context"
        />
      </div>
    </div>
  );
};
